import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import styled from 'styled-components';
import ButtonPrimary from '../../components/Buttons/Button';
import IraFundAccountSelect from '../../components/FundAccountSelect/IraFundAccountSelect';
import Link from '../../components/Link';
import Modal from '../../components/Modal';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { ParagraphNormalSub } from '../../components/Paragraph';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import useApplicationUid from '../../hooks/useApplicationUid';
import { useMenu } from '../../hooks/useMenu';
import useStepPost from '../../hooks/useStepPost';
import { deleteIraApplicationFunding } from '../../store/actions/iraFunding/iraFundingActions';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const ParagraphModal = styled(ParagraphNormalSub)`
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr;
  grid-gap: 1.5rem;
  margin-top: 1rem;

  @media only screen and (max-width: 750px) {
    margin-top: auto;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }
`;

const FundRetirement = () => {
  const { t } = useTranslation();
  const { activeMenu, activeItem } = useMenu();
  const { onSkipStep } = useStepPost({
    skipStepPath: activeItem?.skipPath,
  });
  const dispatch = useDispatch();
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const ApplicantData = useApplicantData();
  const { ApplicationUid } = useApplicationUid();
  const { BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);

  const [isSkipOpen, setSkipOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Fund your account',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Fund your account - Skip funding',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSkipConfirm = () => {
    handleGtmEvent();
    const { IRAFundingType } = IraApplicationFundingResponse;
    if (!IRAFundingType) {
      onSkipStep();
      return;
    }

    dispatch(
      deleteIraApplicationFunding({
        ApplicationUid,
        onSuccess: onSkipStep,
      }),
    );
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      canSkip
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('fundAccount.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <IraFundAccountSelect
        nextPath={activeItem?.nextPath}
        onSkipStep={onSkipStep}
        onSkip={onSkipConfirm}
      />
      <Modal
        isOpen={isSkipOpen}
        center
        onClose={() => setSkipOpen(false)}
        label={t('fundAccount.skipModalTitle')}
        isSmall
      >
        <ParagraphModal>{t('fundAccount.skipModalSubtitle')}</ParagraphModal>
        <ButtonContainer>
          <ButtonPrimary
            title={t('fundAccount.skipSkip')}
            onClick={() => setSkipOpen(false)}
          />
          <Link onClick={onSkipConfirm} title={t('fundAccount.SKIP')} />
        </ButtonContainer>
      </Modal>
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

export default FundRetirement;
